import {
	AudioApi,
	JobsApi,
	VideoApi,
	Configuration,
	FilesApi,
	TextAssistantApi,
	SearchAssistantApi
} from '@plant/genai-api-client';

class AIService {
	constructor(token) {
		this.basePath = window.__env__.VUE_APP_GENAI_BASE_PATH;
		const configuration = new Configuration({
			basePath: this.basePath,
			baseOptions: {
				headers: { Authorization: `Bearer ${token}` }
			}
		});
		const fileConfiguration = new Configuration({
			basePath: this.basePath,
			baseOptions: {
				responseType: 'arraybuffer',
				headers: {
					Authorization: `Bearer ${token}`
				}
			}
		});

		this.audioApi = new AudioApi(configuration);

		this.jobsApi = new JobsApi(configuration);

		this.videoApi = new VideoApi(configuration);

		this.textAssistantApi = new TextAssistantApi(configuration);

		this.searchAssistantApi = new SearchAssistantApi(configuration);

		this.filesApi = new FilesApi(fileConfiguration);
	}

	static isAIAvailable() {
		const { VUE_APP_AI_AVAILABLE } = window.__env__ || false;
		return VUE_APP_AI_AVAILABLE === 'true';
	}

	async getAudioVoices() {
		try {
			const result = await this.audioApi.audioControllerGetVoices();
			return result.data;
		} catch (error) {
			throw new Error(error);
		}
	}

	generateAudioFrom(text, voice = 'en-US-AvaNeural') {
		return new Promise(async (resolve, reject) => {
			try {
				const audioResult = await this.audioApi.audioControllerGenerateAudio({ text, voiceId: voice });
				const newJobId = audioResult.data.value;

				const jobsResult = await this.pollJobUntilComplete(newJobId);

				const audioUrl = jobsResult.data.response.audioUrl;

				resolve({
					voice,
					text,
					audioUrl,
					format: 'audio/mpeg',
					jobId: newJobId
				});
			} catch (error) {
				reject(error);
			}
		});
	}

	async getVideoAvatars() {
		try {
			const result = await this.videoApi.videoControllerGetVideoAvatars();
			return result.data;
		} catch (error) {
			throw new Error(error);
		}
	}

	generateAvatarFrom(text, voiceId, characterId, characterStyle) {
		return new Promise(async (resolve, reject) => {
			try {
				const config = {
					text,
					voiceId,
					characterId,
					characterStyle
				};
				const avatarResult = await this.videoApi.videoControllerGenerateVideo(config);
				const newJobId = avatarResult.data.value;
				resolve(newJobId);
			} catch (error) {
				reject(error);
			}
		});
	}

	pollJobUntilComplete(jobId, maxSeconds = 60) {
		return new Promise((resolve, reject) => {
			let elapsed = 0;
			const interval = setInterval(async () => {
				elapsed++;

				try {
					const result = await this.jobsApi.jobsControllerGetJobById(jobId);

					if (result.data.status === aiActionsStatus.COMPLETE) {
						clearInterval(interval);
						resolve(result);
					}

					if (result.data.status === aiActionsStatus.FAILED) {
						clearInterval(interval);
						reject(new Error('Job failed'));
					}

					if (elapsed >= maxSeconds) {
						clearInterval(interval);
						reject(new Error('Polling time out'));
					}
				} catch (err) {
					clearInterval(interval);
					reject(err);
				}
			}, 1000);
		});
	}

	async getJobBy(id) {
		try {
			const result = await this.jobsApi.jobsControllerGetJobById(id);

			return result.data;
		} catch (error) {
			throw new Error(error);
		}
	}

	async getAllJobs(sortingDirection = 'descending', pageNumber = 1) {
		const jobs = await this.jobsApi.jobsControllerFindJobs(sortingDirection, pageNumber);
		return jobs.data;
	}

	getFileBy(url) {
		return this.filesApi.v1StaticFileRouteGet(url);
	}

	async reWriteText(text, command) {
		try {
			const result = await this.textAssistantApi.textAssistantControllerRewriteText({ text, instructions: [command] });
			const newJobId = result.data.value;

			const jobResult = await this.pollJobUntilComplete(newJobId);

			return jobResult.data;
		} catch (error) {
			throw new Error(error);
		}
	}

	async search(text, numberOfResults) {
		try {
			// const results = [
			// 	{
			// 		custom_id: 'TRADICIONAL',
			// 		text: 'El hummus es una crema suave elaborada con garbanzos cocidos, tahini (pasta de sésamo), jugo de limón y ajo, típica de la cocina árabe y del Medio Oriente. Se sirve como acompañamiento o dip junto con pan pita.'
			// 	},
			// 	{
			// 		custom_id: 'SENSORIAL',
			// 		text: 'Es una mezcla aterciopelada que combina la textura cremosa de los garbanzos con el toque ácido del limón y el aroma intenso del ajo, coronada con un hilo de aceite de oliva que realza su sabor.'
			// 	},
			// 	{
			// 		custom_id: 'CULTURAL',
			// 		text: 'El hummus es más que un alimento: es un símbolo de hospitalidad en la mesa árabe, compartido en reuniones familiares y sociales, donde se disfruta acompañado de verduras frescas, carnes o panes tradicionales.'
			// 	},
			// 	{
			// 		custom_id: 'GLOBAL',
			// 		text: 'Aunque nació en el Medio Oriente, hoy el hummus se ha convertido en un plato internacional, presente en restaurantes y hogares de todo el mundo como alternativa saludable y versátil.'
			// 	},
			// 	{
			// 		custom_id: 'NUTRICIONAL',
			// 		text: 'Es una fuente rica en proteínas vegetales, fibra y grasas saludables, lo que lo convierte en un alimento ideal para dietas equilibradas, veganas o vegetarianas, sin perder su sabor auténtico.'
			// 	}
			// ];

			const result = await this.searchAssistantApi.searchAssistantControllerRequestSearch({ text, numberOfResults });
			const newJobId = result.data.value;

			const jobResult = await this.pollJobUntilComplete(newJobId);

			return jobResult.data;
		} catch (error) {
			throw new Error(error);
		}
	}
}

export default AIService;

/**
 * @enum {string}
 */
export const aiActionsStatus = Object.freeze({
	COMPLETE: 'COMPLETE',
	FAILED: 'FAILED',
	INPROGRESS: 'INPROGRESS',
	CREATED: 'CREATED'
});

/**
 * @enum {string}
 */
export const aiActionsStatusColorMap = Object.freeze({
	COMPLETE: 'success',
	FAILED: 'error',
	INPROGRESS: 'warning',
	CREATED: 'warning'
});
